import { useCallback, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { ProductLabelPrintJob, useDispatchPrintingSettings } from 'src/shared/printing/PrintingManager';
import type { DispatchPrintingSettings, ProductLabelPrintResult } from 'src/shared/printing/PrintingManager';

// Each button owns its popup. No provider, route wrapper or caller changes.
export const ProductLocationPrintButton = ({ productId, available, location, disabled = false, tabIndex = 0 }: LabelRequest & { disabled?: boolean; tabIndex?: number }) => {
  const printingSettings = useDispatchPrintingSettings();
  const [dialog, setDialog] = useState<LabelDialog | null>(null);
  const [job, setJob] = useState<LabelJob | null>(null);
  const [notice, setNotice] = useState<PrintNotice | null>(null);
  const sequence = useRef(0);
  const submitted = useRef(false);
  const copies = Number(dialog?.copies);
  const validCopies = Boolean(dialog?.copies.trim()) && Number.isSafeInteger(copies) && copies > 0;
  const printing = job != null;

  const openDialog = () => {
    if (disabled || !String(productId ?? '').trim() || submitted.current) return;
    const quantity = Number.isFinite(available) ? Math.max(0, available) : 0;
    setNotice(null);
    // Snapshot the product, location and available count on every opening.
    setDialog({ productId, available: quantity, location, copies: String(quantity) });
  };
  const closeDialog = () => {
    if (submitted.current) return;
    setDialog(null);
    setNotice(null);
  };
  const submit = () => {
    if (!dialog || !validCopies || submitted.current || notice) return;
    submitted.current = true;
    setJob({ key: ++sequence.current, productId: dialog.productId, copies, settings: printingSettings });
  };
  const complete = useCallback((result: ProductLabelPrintResult) => {
    if (!job) return;
    const prefix = `Product ${job.productId} (${job.copies} labels): `;
    const outcome = result.outcome;
    const severity = result.error ? 'error' : outcome?.method === 'printer' ? 'success' : 'warning';
    const message = result.error ? `${prefix}${result.error} Close and reopen Print product label to retry.` : outcome?.method === 'printer' ? `${prefix}Sent to "${outcome.printer}".` : `${prefix}${outcome?.method === 'savePdf' ? `PDF downloaded. Print ${job.copies} copies from the downloaded label.` : 'Printing skipped.'}${outcome?.reason ? ` ${outcome.reason}` : ''}`;
    submitted.current = false;
    setNotice({ severity, message });
    setJob(null);
  }, [job]);

  return <>
    <Tooltip title="Print product label"><span style={{ display: 'inline-flex' }}><IconButton type="button" size="small" aria-label="Print product label" tabIndex={tabIndex} disabled={disabled || printing || !String(productId ?? '').trim()} onClick={(event) => { event.stopPropagation(); openDialog(); }}><PrintIcon fontSize="small" /></IconButton></span></Tooltip>
    {dialog && <Dialog open fullWidth maxWidth="xs" onClose={closeDialog} disableEscapeKeyDown={printing} aria-label="Print product label" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); submit(); }}>
        <DialogTitle>Print product label</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>Product {dialog.productId}</Typography>
          {dialog.location && <Typography variant="body2" sx={{ mb: 1 }}>Location: {dialog.location}</Typography>}
          <Typography variant="body2" sx={{ mb: 2 }}>Available at this location: {dialog.available}</Typography>
          <TextField autoFocus fullWidth label="Number of labels" type="number" value={dialog.copies} disabled={printing || notice != null} inputProps={{ min: 1, step: 1 }} error={!validCopies} helperText={!validCopies ? 'Enter a positive whole number.' : 'One label per product.'} onChange={(event) => setDialog({ ...dialog, copies: event.target.value })} />
          {printing && <Alert severity="info" sx={{ mt: 2 }}>Printing product labels…</Alert>}
          {notice && <Alert severity={notice.severity} sx={{ mt: 2 }}>{notice.message}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={closeDialog} disabled={printing}>{notice ? 'Close' : 'Cancel'}</Button>
          {!notice && <Button type="submit" variant="contained" startIcon={<PrintIcon />} disabled={!validCopies || printing}>{printing ? 'Printing…' : 'Print'}</Button>}
        </DialogActions>
      </Box>
      {job && <ProductLabelPrintJob key={job.key} productId={job.productId} copies={job.copies} settings={job.settings} onComplete={complete} />}
    </Dialog>}
  </>;
};

type LabelRequest = { productId: string | number; available: number; location?: string | null };

type LabelDialog = LabelRequest & { copies: string };

type LabelJob = { key: number; productId: string | number; copies: number; settings: DispatchPrintingSettings };

type PrintNotice = { severity: 'success' | 'warning' | 'error'; message: string };
